import React, {FC, Suspense} from 'react';

import {SnackBarsContainer, Theme} from '@optimaxdev/design-system';
import {ErrorBoundary} from '@sentry/react';
import {Provider} from 'react-redux';

import {Routes, Route, matchPath, useLocation} from 'react-router-dom';

import {PersistGate} from 'redux-persist/integration/react';

import {AppCrashFallback} from 'components/appCrashFallback';

import {ButtonsForAddProductWithoutPDP} from 'ONLY_LOCAL_DEV/buttonsForAddProductWithoutPDP';

import {persistor, store} from './store';

const Cart = React.lazy(() => import('./pages/cart'));

const CheckoutPageWrapper = React.lazy(() => import('./pages/checkout/checkoutWrapper'));

const SuccessPageWrapperConnected = React.lazy(() => import('./pages/success/successWrapper'));

type PropsType = {
    skeleton: React.ReactNode;
};

type RoutesType = {
    element: (props: PropsType) => React.ReactElement;
    path: string;
};

const ROUTES: RoutesType[] = [
    {element: () => <Cart />, path: '/checkout/cart'},
    {element: () => <CheckoutPageWrapper />, path: '/checkout/onepage'},
    {
        element: ({skeleton}) => <SuccessPageWrapperConnected skeleton={skeleton} />,
        path: '/checkout/onepage/success',
    },
    {element: () => <CheckoutPageWrapper />, path: '/paypal/express/review/'},
    {element: () => <CheckoutPageWrapper />, path: '/optimaxcheckout/amazon'},
    {element: () => <CheckoutPageWrapper />, path: '/checkout/onepage/address/:type/:action'},
];

/**
 * Wrapper component for routes
 */
const DynamicRoutesWrapper: FC<PropsType> = ({skeleton}) => {
    const {pathname} = useLocation();

    const matchedRoute = ROUTES.find(route => matchPath(route.path, pathname));

    // TODO: need to remove fallback of skeleton (|| div) when skeleton reaches to prod
    return (
        <Suspense fallback={skeleton || <div style={{height: '100vh'}} />}>
            <Theme theme={{isBlueTheme: true, isMobile: false}}>
                <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                        <ErrorBoundary
                            fallback={() => (
                                <AppCrashFallback
                                    selector={state =>
                                        state.checkoutSideReview?.completeOrder?.order_id
                                    }
                                    platform="desktop"
                                />
                            )}
                        >
                            {IS_LOCAL ? (
                                <Routes>
                                    <Route element={<ButtonsForAddProductWithoutPDP />} path="/" />
                                    {ROUTES.map(({element, path}) => (
                                        <Route
                                            element={element({skeleton})}
                                            key={path}
                                            path={path}
                                        />
                                    ))}
                                </Routes>
                            ) : (
                                matchedRoute && matchedRoute.element({skeleton})
                            )}
                        </ErrorBoundary>
                    </PersistGate>
                    <SnackBarsContainer />
                </Provider>
            </Theme>
        </Suspense>
    );
};

export default DynamicRoutesWrapper;
